'use client'

import * as React from 'react'
import { Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { StatusBadge, type StatusVariant } from '@/components/ui/status-badge'
import { UserText } from '@/components/ui/user-text'
import { formatLearnerDate, formatLearnerNumber } from '@/components/user-portal/learner-i18n'
import { richText } from '@/lib/i18n/rich-text'
import { formatActivityExact, formatActivityRelative } from '@/lib/time/activity-time'
import { parseApiDate } from '@/lib/time/parse-api-date'
import { useTranslation } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { handled, isOpenState, isProgressing, type ActivityDto, type ActivityState } from '@/lib/activities/types'
import { rendererFor } from './activity-dock-renderers'

const CLOCK: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }

/**
 * When finished work ended, short: "17:51" today, "Nov 2, 17:51" before (it stays up to 24 hours). The viewer's zone
 * and the interface language, as every instant on an admin screen (docs/LOCALE.md §2.9).
 */
export function finishedClock(value: string | null, locale: string, now: Date = new Date()): string {
  const date = parseApiDate(value)
  if (!date) return ''
  const today = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
  return formatLearnerDate(date, locale, today ? CLOCK : { month: 'short', day: 'numeric', ...CLOCK })
}

const STATE_VARIANT: Record<ActivityState, StatusVariant> = {
  queued: 'info',
  running: 'info',
  paused: 'warning',
  finished: 'success',
  finished_with_failures: 'warning',
  cancelled: 'muted',
  failed: 'destructive',
}

/** The stacked bar: done (green), failed (red), skipped (muted), over a track of what is left. */
export function ActivityBar({ counts, label }: { counts: ActivityDto['counts']; label: string }) {
  const pct = (n: number) => (counts.total > 0 ? Math.min(100, (n / counts.total) * 100) : 0)
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={counts.total}
      aria-valuenow={handled(counts)}
      className="flex h-1.5 w-full overflow-hidden rounded-full bg-primary/20"
    >
      <div className="h-full bg-emerald-500 transition-all motion-reduce:transition-none" style={{ width: `${pct(counts.done)}%` }} />
      <div className="h-full bg-destructive transition-all motion-reduce:transition-none" style={{ width: `${pct(counts.failed)}%` }} />
      <div className="h-full bg-foreground/20 transition-all motion-reduce:transition-none" style={{ width: `${pct(counts.skipped)}%` }} />
    </div>
  )
}

interface ActivityCardProps {
  activity: ActivityDto
  expanded: boolean
  onToggle: () => void
  onDismiss: (a: ActivityDto) => void
}

/**
 * One activity in the drawer: the module, the title, the state, the bar and how far along it is;
 * opened, the figures (the server's, never recomputed), the failures by reason, who started it and
 * when, and the module's own buttons. The chrome is shared; the words and the buttons come from the
 * module's renderer.
 */
export function ActivityCard({ activity, expanded, onToggle, onDismiss }: ActivityCardProps) {
  const { t, locale } = useTranslation()
  const n = (v: number) => formatLearnerNumber(v, locale)
  const r = rendererFor(activity.kind)
  const c = activity.counts
  const open = isOpenState(activity.state)
  const bodyId = `activity-body-${activity.id}`
  const titleText = r.titleText(activity, t, n)

  const rightText = open
    ? t('activities.card.left', { count: c.remaining })
    : activity.state === 'cancelled'
      ? t('activities.card.notDone', { count: Math.max(0, c.total - c.done) })
      : c.failed > 0
        ? t('activities.card.failedCount', { count: c.failed })
        : ''

  const failedTotal = activity.failedByReason.reduce((sum, x) => sum + x.count, 0)
  const startedWhen = activity.startedAt ? formatActivityRelative(activity.startedAt, t, locale) : ''
  const startedExact = activity.startedAt ? formatActivityExact(activity.startedAt, locale) : ''
  const by = activity.startedBy?.name
  const endedAt = open ? '' : finishedClock(activity.finishedAt, locale)

  return (
    <Card
      className="gap-0 py-0 shadow-sm"
      data-activity-id={activity.id}
      data-activity-state={activity.state}
    >
      {/* The button holds only text: a button's children are presentational, so the bar (a progressbar) sits beside it. */}
      <div className="px-4 pt-4">
        <div className="-mx-2 flex items-start gap-1">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={onToggle}
            className="block min-w-0 flex-1 rounded-lg px-2 py-1 text-start hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <span className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">{r.moduleLabel(t)}</span>
              <span data-activity-state-badge className="inline-flex shrink-0 whitespace-nowrap">
                <StatusBadge
                  variant={STATE_VARIANT[activity.state]}
                  className="shrink-0 whitespace-nowrap"
                  icon={isProgressing(activity.state) ? <Loader2 className="size-3 animate-spin motion-reduce:animate-none" aria-hidden /> : undefined}
                >
                  {t(`activities.state.${activity.state}`)}
                </StatusBadge>
              </span>
            </span>
            <span className="mt-0.5 block text-sm font-semibold">{r.title(activity, t, n)}</span>
          </button>
          {/* Work that is over is cleared from the card itself, opened or not: it stays until somebody does. */}
          {!open && (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              className="mt-0.5 shrink-0 whitespace-nowrap text-muted-foreground"
              aria-label={t('activities.card.clearAria', { title: titleText })}
              onClick={() => onDismiss(activity)}
              data-activity-clear
            >
              <X className="size-3.5" aria-hidden />
              {t('activities.card.clear')}
            </Button>
          )}
        </div>
        <div className="mt-2.5">
          <ActivityBar counts={c} label={titleText} />
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2 pb-4 text-xs tabular-nums text-muted-foreground">
          {open ? (
            <span>{t(`activities.card.progress.${activity.unit}`, { done: n(handled(c)), total: n(c.total) })}</span>
          ) : (
            // Finished: what it did and when it ended, on one line (owner, 3 Nov 2026, Frame 5: "82 moved to Trash · 17:51").
            <span data-activity-result className="min-w-0 truncate whitespace-nowrap">
              {r.resultLine ? r.resultLine(activity, t, n) : t(`activities.card.progress.${activity.unit}`, { done: n(handled(c)), total: n(c.total) })}
              {endedAt && (
                <>
                  {' '}
                  <span aria-hidden>·</span>{' '}
                  <time dateTime={activity.finishedAt ?? undefined} title={formatActivityExact(activity.finishedAt, locale)}>
                    {endedAt}
                  </time>
                </>
              )}
            </span>
          )}
          <span className="shrink-0 whitespace-nowrap">{rightText}</span>
        </div>
      </div>

      <div id={bodyId} hidden={!expanded} className={cn('space-y-3 px-4 pb-4', !expanded && 'hidden')}>
        {expanded && (
          <>
            <div className="grid grid-cols-2 gap-2">
              {r.tiles(activity, t, n).map((tile) => (
                <div key={tile.key} className="rounded-lg border px-3 py-2" data-activity-tile={tile.key}>
                  <div className={cn('text-lg font-bold tabular-nums', tile.tone === 'bad' && 'text-destructive')}>{tile.value}</div>
                  <div className="text-xs text-muted-foreground">{tile.label}</div>
                </div>
              ))}
            </div>

            <div data-activity-failures>
              <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>{t('activities.card.failedByReason')}</span>
                <span className="tabular-nums">{n(c.failed)}</span>
              </div>
              {c.failed === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">{t('activities.card.noFailures')}</p>
              ) : failedTotal === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">{t('activities.card.noReasons')}</p>
              ) : (
                <ul className="mt-1 divide-y text-sm">
                  {activity.failedByReason.map((x) => (
                    <li key={`${x.code}-${x.retryable}`} className="flex items-start justify-between gap-3 py-1.5">
                      <span>
                        {r.reasonLabel(t, x.code)}
                        {x.retryable === true && <span className="text-muted-foreground"> · {t('activities.card.canRetry')}</span>}
                        {x.retryable === false && <span className="text-muted-foreground"> · {t('activities.card.cannotRetry')}</span>}
                      </span>
                      <span className="font-medium tabular-nums">{n(x.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {(startedWhen || by) && (
              <p className="text-xs text-muted-foreground" title={startedExact || undefined}>
                {by
                  ? richText(t('activities.card.startedBy'), { name: <UserText>{by}</UserText>, when: startedWhen })
                  : t('activities.card.startedAt', { when: startedWhen })}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <r.Actions activity={activity} />
            </div>
          </>
        )}
      </div>
    </Card>
  )
}
